import { stringifyURLQuery, uniqBy } from '@seiue/util'
import { LocationState } from 'history'
import {
  useHistory as useHistoryOriginal,
  useParams as useParamsRaw,
  useLocation as useLocationOriginal,
} from 'react-router-dom'

import {
  ModalRouteNode,
  RouteConfig,
  RouteNode,
  RouteParams,
  RouteParamsList,
} from './types'
import { formatParams, parseParams, sortRoutes } from './utils'

export * from './types'
export * from './utils'
export * from './hooks'

let nameToRoute: { [key: string]: RouteNode } | null = null

const genNameToRoute = (routesRaw: RouteNode[]) => {
  const map: { [key: string]: RouteNode } = {}
  const routesToMap = (curRoutes: RouteNode[]) => {
    if (!curRoutes) return {}
    curRoutes.forEach((r: RouteNode) => {
      if (r.name) {
        if (map[r.name] != null) {
          /*
           * if (isProduction()) {
           *   // eslint-disable-next-line
           *   console.error('路由名重复')
           * } else {
           */
          throw new Error(
            // eslint-disable-next-line
            `路径 ${r.path} 的路由名称： ${r.name} 已经存在, 请修改为唯一值`
          )
          // }
        }

        map[r.name] = r
      }

      return r.subRoutes && routesToMap(r.subRoutes)
    })

    return map
  }

  return routesToMap(routesRaw)
}

export const convertRoute = (
  route: RouteConfig,
  parent?: RouteNode
): RouteNode => {
  const { subRoutes, name, path, ...otherConfig } = route
  const converted: RouteNode = {
    ...otherConfig,
    path: `${parent?.path || ''}/${path}`,
    exact: !route.subRoutes,
    parentPath: parent?.path,
    name: name
      ? parent && parent.name
        ? `${parent.name}.${name}`
        : name
      : undefined,
  }

  if (subRoutes) {
    converted.subRoutes = subRoutes.map<RouteNode>((sr) =>
      convertRoute(sr, converted)
    )
  }

  return converted
}

export const routes: RouteNode[] = []

export const modalRoutes: ModalRouteNode[] = []

export const addRoutes = (
  newRoutes?: RouteConfig[],
  newModalRoutes?: ModalRouteNode[]
) => {
  const convertedRoutes = newRoutes?.map((r) => convertRoute(r)) || []

  if (convertedRoutes.length) {
    routes.push(...convertedRoutes)
    sortRoutes(routes)
  }

  if (newModalRoutes) modalRoutes.push(...newModalRoutes)
  return {
    routes: convertedRoutes,
    modalRoutes: newModalRoutes,
  }
}

export const getPath = <
  TRouteParamsList extends RouteParamsList,
  TName extends keyof TRouteParamsList
>(
  name: TName,
  params?: TRouteParamsList[TName]
) => {
  nameToRoute = nameToRoute ?? genNameToRoute(uniqBy(routes, 'path'))

  let { path } = nameToRoute[name as any]

  if (!params) return path

  const query: { [key: string]: string } = {}
  let hasQuery = false

  Object.entries(formatParams(params as RouteParams)).forEach(([key, val]) => {
    if (val == null) return

    const endExp = new RegExp(`:${key}$`)
    const midExp = new RegExp(`:${key}/`)

    if (path.match(endExp)) {
      path = path.replace(endExp, val)
    } else if (path.match(midExp)) {
      path = path.replace(midExp, `${val}/`)
    } else {
      query[key] = val
      hasQuery = true
    }
  })

  return hasQuery ? `${path}?${stringifyURLQuery(query)}` : path
}

export const useHistory = <TRouteParamsList extends RouteParamsList>() => {
  const history = useHistoryOriginal()

  return {
    push: <TName extends keyof TRouteParamsList>(
      name: TName,
      params?: TRouteParamsList[TName],
      state?: LocationState
    ) => history.push(getPath(name, params), state),

    replace: <TName extends keyof TRouteParamsList>(
      name: TName,
      params?: TRouteParamsList[TName],
      state?: LocationState
    ) => history.replace(getPath(name, params), state),

    goBack: () => {
      if (history.length <= 1) {
        /*
         * 如果长度为 1，则将当前路由改变为 Home
         * 然后向内推入一个当前路由的信息
         * 最后正常执行后退，回到首页
         */

        const currentPath = `${history.location.pathname}${history.location.search}`
        const currentState = history.location.state
        history.replace('/')
        history.push(currentPath, currentState)
      }

      history.goBack()
    },
  }
}

export const useParams = <
  TRouteParamsList extends RouteParamsList,
  TName extends keyof TRouteParamsList
>(): TRouteParamsList[TName] =>
  parseParams(useParamsRaw()) as TRouteParamsList[TName]

export const useLocation = useLocationOriginal
